/**
 * Tutor official-documents form section.
 * Criminal-record upload (create vs edit paths), metadata fields, and validate checkbox.
 */
import * as React from 'react';
import { FileSignature } from 'lucide-react';
import { FileDropzone } from '@/components/common/FileDropzone';
import { Input } from '@/components/forms/Input';
import { DatePicker } from '@/components/forms/DatePicker';
import { Checkbox } from '@/components/forms/Checkbox';
import { TUTOR_CRIMINAL_RECORD_UPLOAD_CONFIG } from '@/components/tutors/tutor-configs';
import { defaultUpload, fileNameFromUrl } from '@/components/tutors/inperson/tutor-form-utils';
import {
  buildCriminalRecordPatch,
  fetchCriminalRecordSignedUrl,
  postCriminalRecordFile,
} from '@/utils/criminal-record-utils';
import type { CriminalRecordUploadInfo } from '@/types/user.types';

/** Props for {@link CriminalRecordField}. */
interface CriminalRecordFieldProps {
  tutorUserId: string;
  existingRecord?: CriminalRecordUploadInfo | null;
  onUploaded?: (info: CriminalRecordUploadInfo) => void;
  onBatchChange?: (patch: Record<string, any>) => void;
  immediateUploadFn?: (file: File) => Promise<string>;
  onUrlUploaded?: (url: string | null) => void;
  onFileSelect?: (file: File | null) => void;
}

/** Immediate-upload dropzone for the criminal-record file (create vs edit paths differ). */
function CriminalRecordField({
  tutorUserId,
  existingRecord,
  onUploaded,
  onBatchChange,
  immediateUploadFn,
  onUrlUploaded,
  onFileSelect,
}: Readonly<CriminalRecordFieldProps>) {
  const [uploaded, setUploaded] = React.useState<CriminalRecordUploadInfo | null>(null);
  const [signedUrl, setSignedUrl] = React.useState<string | null>(null);
  /** Latest upload wins over the existing record. */
  const current = uploaded ?? existingRecord ?? null;
  /** Create mode has no user id yet — upload goes through `immediateUploadFn`. */
  const isCreateMode = !tutorUserId && !!immediateUploadFn;

  /** Fetch a signed URL so the dropzone can preview the stored file. */
  const loadSignedUrl = React.useCallback(async () => {
    const url = await fetchCriminalRecordSignedUrl(tutorUserId);
    setSignedUrl(url);
  }, [tutorUserId]);

  React.useEffect(() => {
    if (tutorUserId && current?.fileName) {
      void loadSignedUrl();
    }
  }, [tutorUserId, current?.fileName, loadSignedUrl]);

  /** Edit-mode upload: POST the file then patch the form. */
  const editUploadFn = React.useCallback(
    async (file: File): Promise<string> => {
      const info = await postCriminalRecordFile(tutorUserId, file);
      setUploaded(info);
      if (onBatchChange) {
        onBatchChange(buildCriminalRecordPatch(info));
      } else {
        onUploaded?.(info);
      }
      void loadSignedUrl();
      return info.fileName;
    },
    [tutorUserId, onBatchChange, onUploaded, loadSignedUrl],
  );

  /** Active upload handler: create uses `immediateUploadFn`, edit uses {@link editUploadFn}. */
  let activeUploadFn: ((file: File) => Promise<string>) | undefined;
  if (isCreateMode) activeUploadFn = immediateUploadFn;
  else if (tutorUserId) activeUploadFn = editUploadFn;

  return (
    <FileDropzone
      label="Casier judiciaire (PDF)"
      required
      accept={TUTOR_CRIMINAL_RECORD_UPLOAD_CONFIG.accept}
      maxSize={TUTOR_CRIMINAL_RECORD_UPLOAD_CONFIG.maxSize}
      uploadFn={activeUploadFn}
      onFileSelect={onFileSelect}
      value={current?.fileName ?? undefined}
      previewUrl={signedUrl ?? undefined}
      onChange={
        isCreateMode
          ? (url) => onUrlUploaded?.(url)
          : (url) => {
              if (!url) {
                setUploaded(null);
                setSignedUrl(null);
                onBatchChange?.(buildCriminalRecordPatch(null));
              }
            }
      }
      disabled={!activeUploadFn && !onFileSelect}
    />
  );
}

/** Props for {@link TutorOfficialDocumentsSection}. */
interface Props {
  mode: 'create' | 'edit';
  tutorUserId: string | undefined;
  criminalRecord: Record<string, any>;
  allCriminalRecordFilled: boolean;
  set: (field: string, value: any) => void;
  setCriminalRecord: (key: string, value: any) => void;
  setBatch?: (patch: Record<string, any>) => void;
  onFileSelect?: (key: string, file: File | null) => void;
}

/** Tutor official-documents form section. */
export function TutorOfficialDocumentsSection({
  mode,
  tutorUserId,
  criminalRecord,
  allCriminalRecordFilled,
  set,
  setCriminalRecord,
  setBatch,
  onFileSelect,
}: Readonly<Props>) {
  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <FileSignature className="w-4 h-4" />
        Casier judiciaire
      </h4>
      <CriminalRecordField
        tutorUserId={mode === 'edit' ? (tutorUserId ?? '') : ''}
        existingRecord={
          criminalRecord.fileName
            ? {
                fileName: criminalRecord.fileName,
                depositedAt: criminalRecord.uploadDate ?? new Date().toISOString(),
                status: criminalRecord.status ?? 'pending',
                validatedAt: criminalRecord.validatedAt ?? null,
              }
            : null
        }
        immediateUploadFn={mode === 'create' && !onFileSelect ? defaultUpload : undefined}
        onFileSelect={
          mode === 'create' && onFileSelect
            ? (file) => onFileSelect('criminalRecordFile', file)
            : undefined
        }
        onBatchChange={setBatch}
        onUrlUploaded={
          mode === 'create' && !onFileSelect
            ? (url) => {
                if (setBatch) {
                  if (url) {
                    const name = fileNameFromUrl(url);
                    setBatch({
                      criminalRecordDocumentLink: url,
                      'criminalRecord.fileName': name,
                      'criminalRecord.documentName': name,
                      'criminalRecord.uploadDate': new Date().toISOString().slice(0, 10),
                      'criminalRecord.status': 'pending',
                    });
                  } else {
                    setBatch({
                      criminalRecordDocumentLink: '',
                      'criminalRecord.fileName': '',
                      'criminalRecord.documentName': '',
                    });
                  }
                } else {
                  set('criminalRecordDocumentLink', url);
                  if (url) {
                    const name = fileNameFromUrl(url);
                    setCriminalRecord('fileName', name);
                    setCriminalRecord('documentName', name);
                    setCriminalRecord('uploadDate', new Date().toISOString().slice(0, 10));
                    setCriminalRecord('status', 'pending');
                  } else {
                    setCriminalRecord('fileName', '');
                    setCriminalRecord('documentName', '');
                  }
                }
              }
            : undefined
        }
        onUploaded={(info) => {
          setCriminalRecord('documentName', info.fileName);
          setCriminalRecord('fileName', info.fileName);
          setCriminalRecord('uploadDate', info.depositedAt.slice(0, 10));
          setCriminalRecord('status', info.status);
        }}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Numéro de casier"
          placeholder="Ex: 123456789"
          value={criminalRecord.recordNumber ?? ''}
          onChange={(e) => setCriminalRecord('recordNumber', e.target.value)}
          inputSize="sm"
        />
        <DatePicker
          label="Date d'émission"
          placeholder="JJ/MM/AAAA"
          value={criminalRecord.issueDate ?? ''}
          onChange={(val) => setCriminalRecord('issueDate', val)}
          inputSize="sm"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 items-end">
        <Input
          label="Délivré par"
          placeholder="Ex: Casier judiciaire national"
          value={criminalRecord.issuedBy ?? ''}
          onChange={(e) => setCriminalRecord('issuedBy', e.target.value)}
          inputSize="sm"
        />
        <Checkbox
          label="Engagement sur l'honneur"
          checked={criminalRecord.honorCommitment === true}
          onCheckedChange={(checked) => setCriminalRecord('honorCommitment', checked)}
          size="sm"
        />
      </div>
      <Checkbox
        label="Marquer comme validé"
        checked={criminalRecord.markValidated === true}
        onCheckedChange={(checked) => setCriminalRecord('markValidated', checked)}
        disabled={!allCriminalRecordFilled}
        size="sm"
      />
    </div>
  );
}
